import { useCallback, useEffect, useRef, useState } from 'react';
import { TreeView } from '@phosphor-icons/react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BodyContainer } from '../../../../elements';
import { InventoryListHook } from '../../../../hooks/inventory';
import { useSecondaryHeader } from '../../../../hooks/useSecondaryHeader';
import {
  AddInventoryPopup,
  EditAttributePopup,
  EditBarcodeIdentifiers,
  EditRestockThreshold,
  ExportItemLogs,
  InventoryExportFeeds,
  InventoryAdjustmentPopup,
} from '../../../../page-components';
import { InventoryListTable } from '../../../../page-components/inventory/list/InventoryListTable';
import { InventoryListToolbar } from '../../../../page-components/inventory/list/InventoryListToolbar';
import { CommonUtility } from '../../../../utility';
import { useDebouncedMemo } from '../../../../hooks';
import { EditLotAttributePopup } from '../../../../page-components/inventory/LotAttributePopup';
import { EditPallet } from '../../../../page-components/inventory/EditPallet';
import { useGlobalContext } from '../../../../contexts';

export function InventoryListScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [adjustmentItem, setAdjusmentItem] = useState(null);
  const { warehouseId } = useGlobalContext();
  const [addInventoryPopup, setAddInventoryPopup] = useState(false);

  const [editItemPopup, setEditItem] = useState(null);
  const [imperial, setImperial] = useState(true);
  const [editLotPopup, setEditLotPopup] = useState(null);
  const [editIdentifiers, setEditIdentifiers] = useState(null);
  const [editRestockItem, setEditRestockItem] = useState(null);
  const [editPalletPopup, setEditPalletPopup] = useState(null);
  const [itemForLog, setItemForLog] = useState(null);
  const [exportFeedsPopup, setExportFeedsPopup] = useState(false);

  const openAdjustmentPopup = item => {
    setAdjusmentItem(item);
  };

  const closeAdjustmentPopup = () => {
    setAdjusmentItem(null);
  };

  const {
    data,
    loading,
    totalData,
    filter,
    filterChanged,
    pageChanged,
    refresh,
    updateAllFilter,
  } = InventoryListHook(warehouseId);

  const setFilter = useCallback(
    next => {
      updateAllFilter({ ...next, page: 1 });
    },
    [updateAllFilter],
  );

  const listFilters = useDebouncedMemo(
    () => {
      const query = Object.fromEntries(searchParams.entries());
      const temp = { ...query };
      Object.keys(temp).forEach(key => {
        const final = CommonUtility.tryParseJSONObject(temp[key]);
        if (final) {
          temp[key] = final;
        }
      });

      const defaultValues = { ...temp };

      return [
        {
          name: 'isDeleted',
          label: 'Item Status',
          icon: <TreeView size={16} />,
          options: [
            { label: 'Show Deleted Items', value: 'true' },
            { label: 'Show All Active Items', value: 'false' },
          ],
          defaultValue: defaultValues.isDeleted ?? 'false',
          ignoreDefault: 'false',
        },
        {
          skip: true,
          name: 'page',
          label: 'Page',
          icon: null,
          defaultValue: defaultValues.page ?? 1,
          ignoreDefault: 1,
        },
        {
          skip: true,
          name: 'limit',
          label: 'Limit',
          icon: null,
          defaultValue: CommonUtility.isValidNumber(defaultValues.limit)
            ? Number(defaultValues.limit)
            : 10,
          ignoreDefault: 10,
        },
      ];
    },
    [searchParams],
    500,
  );

  const didHydrateFromUrl = useRef(false);

  useEffect(() => {
    if (didHydrateFromUrl.current) return;
    didHydrateFromUrl.current = true;

    const query = Object.fromEntries(searchParams.entries());
    if (Object.keys(query).length) {
      updateAllFilter(query);
    }
  }, [searchParams, updateAllFilter]);

  useEffect(() => {
    const urlObject = {};
    Object.keys(filter)?.forEach(key => {
      const value = filter[key];
      const config = listFilters?.find(f => f.name === key);
      if (
        value !== undefined &&
        value !== null &&
        value !== '' &&
        config?.ignoreDefault !== value
      ) {
        urlObject[key] = value;
      }
    });
    const nextSearch = new URLSearchParams(urlObject).toString();
    if (searchParams.toString() === nextSearch) return;

    navigate({ search: nextSearch }, { replace: true });
  }, [filter, listFilters, navigate, searchParams]);

  const editItem = item => {
    setEditItem(item);
  };

  const editPallet = item => {
    setEditPalletPopup(item);
  };

  const editBarcodes = item => {
    setEditIdentifiers(item);
  };

  const editRestock = item => {
    setEditRestockItem(item);
  };

  const openInventoryPopup = useCallback(() => {
    setAddInventoryPopup(true);
  }, []);

  const onExport = useCallback(() => {
    setExportFeedsPopup(true);
  }, []);

  const openItemPopupLogs = item => {
    setItemForLog(item);
  };

  const closeItemPopupLog = () => {
    setItemForLog(null);
  };

  const onCustomerSelect = useCallback(
    customer => {
      filterChanged('customerOrganizationId', customer.customerOrganizationId);
    },
    [filterChanged],
  );

  const onSearchChange = useCallback(
    value => {
      filterChanged('search', value);
    },
    [filterChanged],
  );

  useSecondaryHeader(
    () => (
      <InventoryListToolbar
        imperial={imperial}
        onImperialChange={setImperial}
        listFilters={listFilters}
        filter={filter}
        setFilter={setFilter}
        onExport={onExport}
        onAddInventory={openInventoryPopup}
        onCustomerSelect={onCustomerSelect}
        search={filter.search ?? ''}
        onSearchChange={onSearchChange}
      />
    ),
    [
      imperial,
      listFilters,
      filter,
      setFilter,
      onExport,
      openInventoryPopup,
      onCustomerSelect,
      onSearchChange,
    ],
  );

  return (
    <BodyContainer className="no-header">
      <InventoryListTable
        data={data}
        loading={loading}
        imperial={imperial}
        filter={filter}
        totalData={totalData}
        pageChanged={pageChanged}
        filterChanged={filterChanged}
        refresh={refresh}
        openPopup={openAdjustmentPopup}
        editItem={editItem}
        editBarcodes={editBarcodes}
        editRestock={editRestock}
        openItemPopupLogs={openItemPopupLogs}
        editLotAttribute={setEditLotPopup}
        editPallet={editPallet}
      />
      <InventoryAdjustmentPopup
        onClose={closeAdjustmentPopup}
        open={!!adjustmentItem}
        item={adjustmentItem}
        refresh={refresh}
        warehouseId={warehouseId}
      />
      <EditAttributePopup
        open={!!editItemPopup}
        onClose={() => setEditItem(null)}
        item={editItemPopup}
        refresh={refresh}
      />
      <EditLotAttributePopup
        open={!!editLotPopup}
        onClose={() => setEditLotPopup(null)}
        item={editLotPopup}
        refresh={refresh}
      />
      <AddInventoryPopup
        open={addInventoryPopup}
        onClose={() => setAddInventoryPopup(false)}
        defaultWarehoueId={warehouseId}
        refresh={refresh}
      />
      <EditBarcodeIdentifiers
        item={editIdentifiers}
        open={!!editIdentifiers}
        onClose={() => setEditIdentifiers(null)}
        refresh={refresh}
      />
      <EditRestockThreshold
        item={editRestockItem}
        open={!!editRestockItem}
        onClose={() => setEditRestockItem(null)}
        refresh={refresh}
        warehouseId={warehouseId}
      />
      <EditPallet
        open={editPalletPopup}
        onClose={() => setEditPalletPopup(null)}
        warehouseId={warehouseId}
        item={editPalletPopup}
        refresh={refresh}
      />
      <ExportItemLogs
        warehouseId={warehouseId}
        onClose={closeItemPopupLog}
        item={itemForLog}
        open={!!itemForLog}
      />
      <InventoryExportFeeds
        open={exportFeedsPopup}
        onClose={() => setExportFeedsPopup(false)}
        warehouseId={warehouseId}
        filters={filter}
      />
    </BodyContainer>
  );
}
